import { AttendanceSheetStatusEnum } from '@/modules/business/attendance-sheet/enums/attendance-sheet-status.enum';
import { listAllMonthsOfAnIntership } from '@/modules/business/attendance-sheet/attendance-sheet.service';
import { inactivateOperationalProfileEntry } from '@/modules/people/presentation/forms/profiles/operational-profiles.utils';
import type { PersonFormDto } from '@/modules/people/presentation/person-form.utils';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { DataIso } from '@/shared/utils/datas';

export type FolhaPendenteInfo = {
  estagioId: number;
  ano: number;
  mes: number;
  status: number;
};

function desligamentoVazio(valor?: string | Date | null): boolean {
  if (valor == null) return true;
  return String(valor).trim() === '';
}

/** Data de hoje no formato do input date (yyyy-MM-dd). */
export function dataHojeIso(): string {
  return DataIso(new Date());
}

/**
 * Lista folhas existentes cujo status não é concluída, para todos os estágios persistidos.
 */
export async function listarFolhasNaoConcluidas(
  estagios: Array<{ id?: number | null }> | undefined | null,
): Promise<FolhaPendenteInfo[]> {
  const ids = (estagios ?? [])
    .map((e) => e.id)
    .filter((id): id is number => typeof id === 'number' && id > 0);

  if (ids.length === 0) return [];

  const resultados = await Promise.all(
    ids.map(async (estagioId) => {
      const meses = await listAllMonthsOfAnIntership(estagioId);
      return meses
        .filter(
          (m) =>
            !!m.folha?.id &&
            m.status != null &&
            m.status !== AttendanceSheetStatusEnum.CONCLUIDA,
        )
        .map((m) => ({
          estagioId,
          ano: m.ano,
          mes: m.mes,
          status: m.status,
        }));
    }),
  );

  return resultados.flat();
}

/**
 * Aplica desligamento no formulário: data nos estágios sem desligamento,
 * inativa o perfil estagiário e desabilita o acesso ao sistema.
 * Persistência ocorre ao salvar a pessoa.
 */
export function aplicarDesligamentoNoFormulario(
  getValues: UseFormGetValues<PersonFormDto>,
  setValue: UseFormSetValue<PersonFormDto>,
  dataDesligamento: string,
): { estagiosAtualizados: number } {
  const data = DataIso(dataDesligamento);
  const estagiarioAtual = getValues('estagiario');
  const estagios = estagiarioAtual?.estagios ?? [];

  let estagiosAtualizados = 0;
  const novosEstagios = estagios.map((estagio) => {
    if (!desligamentoVazio(estagio.desligamento)) return estagio;
    estagiosAtualizados += 1;
    return { ...estagio, desligamento: data };
  });

  const perfis = getValues('perfisOperacionais') ?? [];
  const alvo = perfis.find((item) => {
    if (item.kind !== 'estagiario') return false;
    if (estagiarioAtual?.id != null && item.id === estagiarioAtual.id) return true;
    return item.ativo || item.isDraft;
  });

  const estagiarioPayload = {
    ...estagiarioAtual,
    estagios: novosEstagios,
    ativo: false,
  };

  if (alvo) {
    const basePerfis = alvo.isDraft
      ? perfis.map((item) =>
          item.localKey === alvo.localKey
            ? {
                ...item,
                ativo: false,
                dirty: true,
                data: { ...item.data, ativo: false },
              }
            : item,
        )
      : inactivateOperationalProfileEntry(perfis, alvo.localKey);

    const inativados = basePerfis.map((item) =>
      item.localKey === alvo.localKey
        ? {
            ...item,
            ativo: false,
            dirty: true,
            data: { ...item.data, ...estagiarioPayload, ativo: false },
          }
        : item,
    );
    setValue('perfisOperacionais', inativados, { shouldDirty: true });
  }

  setValue('estagiario', estagiarioPayload as PersonFormDto['estagiario'], {
    shouldDirty: true,
    shouldTouch: true,
  });

  setValue('usuario.ativo', false, { shouldDirty: true, shouldTouch: true });

  return { estagiosAtualizados };
}
